import { v4 as uuidv4 } from 'uuid'
import type { VizDirective } from '../../../Generated/VizDirective.ts'
import type { ReportBlock } from '../../../Generated/ReportBlock.ts'
import type { IChatMessage, IChatState, WorkspaceItemMeta, WorkspaceNote } from './ChatSlice.js'
import type { ChatNode, NodeMap } from './conversationTree.js'

/**
 * The serializable WORKSPACE model. A `Workspace` is the persistable projection of the `chat` slice: enough to
 * "save this chat & resume later, including the rendered graphs + report" WITHOUT persisting any volatile /
 * re-derivable state. It is the on-the-wire shape the {@link WorkspaceStore} holds (through the
 * {@link PayloadCodec} seam) and the substrate the Workspace slices evolve — hence the explicit `version`.
 *
 * W-1 bumped this to v2: the conversation is now a TREE ({@link conversationNodes} + `currentNodeId` +
 * `rootNodeId`) instead of the v1 linear `conversation: IChatMessage[]`. A v1 blob migrates to a single linear
 * node chain on load (see {@link migrateWorkspace}) — no branches, so resuming a v1 workspace is unchanged.
 *
 * W-2 bumped this to v3: the unified Workspace panel adds `notes` (manual note items), `pinnedItemIds` (which
 * derived items the user pinned) and `itemMeta` (editable card title/subtitle overrides for chart/report
 * items). A v2 blob migrates by defaulting all three to empty (see {@link migrateWorkspace}).
 *
 * Deliberately NOT persisted (volatile / re-derived on resume):
 *  - per-message `streaming` (a resumed turn is never mid-stream);
 *  - the derived active-path `messages` cache (rebuilt from the tree by `hydrateWorkspace`);
 *  - `currentReport.html` / `status` (re-rendered from `blocks` by `RenderReportEpic` on resume);
 *  - `openRequests`, `focusedGraphKey`, `comparisonCaveat`, `lastFailedQuestion`, `currentLens`, `composerSeed`.
 */
export const WORKSPACE_VERSION = 3

/** The rendered "view" of a workspace: the charts on the canvas + the report document's blocks. */
export interface WorkspaceView {
  openGraphs: VizDirective[]
  report: { blocks: ReportBlock[]; title?: string }
}

export interface Workspace {
  id: string
  title: string
  /** `${scopeType}:${scopeId}` the workspace belongs to — the `list` filter key. */
  scopeKey: string
  /** ISO-8601 timestamp the snapshot was taken. */
  savedAt: string
  /** The TREE conversation (W-1). */
  conversationNodes: NodeMap
  currentNodeId: string | null
  rootNodeId: string | null
  /** Workspace-global pinned facts bridging branches (W-1). */
  globalContext: string[]
  /** Manual NOTE items in the unified panel (W-2). */
  notes: WorkspaceNote[]
  /** Pinned workspace-item ids (W-2) — chart:`<directiveKey>` / `report` / note:`<noteId>`. */
  pinnedItemIds: string[]
  /** Editable card title/subtitle overrides for the derived chart/report items (W-2). */
  itemMeta: Record<string, WorkspaceItemMeta>
  view: WorkspaceView
  kronaSelections: Record<string, string[]>
  version: number
}

/** The v1 (pre-tree) blob shape, kept only so {@link migrateWorkspace} can read + upgrade an old save. */
interface WorkspaceV1 {
  id: string
  title: string
  scopeKey: string
  savedAt: string
  conversation: IChatMessage[]
  globalContext?: string[]
  view: WorkspaceView
  kronaSelections?: Record<string, string[]>
  version: number
}

/** The lightweight row the "My workspaces" list renders (no conversation/view payload). */
export interface WorkspaceSummary {
  id: string
  title: string
  scopeKey: string
  savedAt: string
}

/** Strip the volatile `streaming` flag before persisting a message (it is never true at rest). */
function serializableMessage(m: IChatMessage): IChatMessage {
  const copy = { ...m }
  delete copy.streaming
  return copy
}

/** Copy the node map, stripping each node message's volatile `streaming` flag. */
function serializableNodes(nodes: NodeMap): NodeMap {
  const out: NodeMap = {}
  for (const [id, node] of Object.entries(nodes)) {
    out[id] = { ...node, message: serializableMessage(node.message) }
  }
  return out
}

/**
 * Pure snapshot of the current `chat` state into a serializable {@link Workspace}. Drops volatile fields (see
 * the module doc). A fresh `id` is minted unless one is supplied (a supplied id lets a future "overwrite this
 * workspace" reuse the same key).
 */
export function snapshotWorkspace(state: IChatState, title: string, id: string = uuidv4()): Workspace {
  return {
    id,
    title,
    scopeKey: state.scopeKey ?? '',
    savedAt: new Date().toISOString(),
    conversationNodes: serializableNodes(state.conversationNodes),
    currentNodeId: state.currentNodeId,
    rootNodeId: state.rootNodeId,
    globalContext: state.globalContext ?? [],
    notes: state.notes ?? [],
    pinnedItemIds: state.pinnedItemIds ?? [],
    itemMeta: state.itemMeta ?? {},
    view: {
      openGraphs: state.openGraphs ?? [],
      report: { blocks: state.currentReport.blocks, title: state.currentReport.title }
    },
    kronaSelections: state.kronaSelections ?? {},
    version: WORKSPACE_VERSION
  }
}

/**
 * Pure hydration of a resumed {@link Workspace} into a `Partial<IChatState>` for the `hydrateWorkspace` reducer.
 * Restores the TREE (nodes + current + root); the reducer rebuilds the linear `messages` cache from it. The
 * report comes back with `html: null` / `status: 'idle'` — the caller re-renders it from `blocks` via the
 * existing report render loop (`applyReportEdits` → `RenderReportEpic`); charts (`openGraphs`) rehydrate directly.
 */
export function hydrateFromWorkspace(ws: Workspace): Partial<IChatState> {
  const nodes: NodeMap = {}
  for (const [id, node] of Object.entries(ws.conversationNodes ?? {})) {
    nodes[id] = { ...node, message: { ...node.message, streaming: false } }
  }
  return {
    scopeKey: ws.scopeKey,
    conversationNodes: nodes,
    currentNodeId: ws.currentNodeId ?? null,
    rootNodeId: ws.rootNodeId ?? null,
    openGraphs: ws.view.openGraphs ?? [],
    globalContext: ws.globalContext ?? [],
    notes: ws.notes ?? [],
    pinnedItemIds: ws.pinnedItemIds ?? [],
    itemMeta: ws.itemMeta ?? {},
    kronaSelections: ws.kronaSelections ?? {},
    currentReport: {
      blocks: ws.view.report?.blocks ?? [],
      title: ws.view.report?.title,
      html: null,
      status: 'idle'
    }
  }
}

/** True for a legacy v1 blob: it carries a linear `conversation` array and no tree. */
function isV1(raw: Workspace | WorkspaceV1): raw is WorkspaceV1 {
  return (raw as { conversationNodes?: unknown }).conversationNodes === undefined
    && Array.isArray((raw as WorkspaceV1).conversation)
}

/** True for a blob predating the W-2 unified panel: it lacks the `notes`/`pinnedItemIds` fields (v2 or older). */
function needsV2ToV3(raw: Workspace): boolean {
  // A runtime v2 blob is typed as Workspace but has these fields absent — read defensively (hence the cast).
  const w = raw as Partial<Workspace>
  return raw.version < WORKSPACE_VERSION || w.pinnedItemIds === undefined || w.notes === undefined
}

/**
 * Migrate a decoded blob to the current shape. Two upgrades, applied as needed:
 *  - v1 → v2 (W-1): a linear `conversation: IChatMessage[]` becomes a TREE — a single LINEAR node chain (each
 *    node's parent = the previous message), `currentNodeId` = the last node, `rootNodeId` = the first. No
 *    branches, so a resumed v1 workspace behaves exactly as it did.
 *  - v2 → v3 (W-2): the unified-panel fields (`notes`, `pinnedItemIds`, `itemMeta`) default to empty.
 * A current (v3) blob is returned unchanged (same reference). Called by the {@link WorkspaceStore} on `get`.
 */
export function migrateWorkspace(raw: Workspace | WorkspaceV1): Workspace {
  if (isV1(raw)) {
    const nodes: NodeMap = {}
    let parentId: string | null = null
    let rootNodeId: string | null = null
    for (const message of raw.conversation) {
      const node: ChatNode = { id: uuidv4(), parentId, message: serializableMessage(message) }
      nodes[node.id] = node
      if (!rootNodeId) rootNodeId = node.id
      parentId = node.id
    }
    return {
      id: raw.id,
      title: raw.title,
      scopeKey: raw.scopeKey,
      savedAt: raw.savedAt,
      conversationNodes: nodes,
      currentNodeId: parentId,
      rootNodeId,
      globalContext: raw.globalContext ?? [],
      notes: [],
      pinnedItemIds: [],
      itemMeta: {},
      view: raw.view ?? { openGraphs: [], report: { blocks: [] } },
      kronaSelections: raw.kronaSelections ?? {},
      version: WORKSPACE_VERSION
    }
  }
  // A tree blob (v2 or v3). Fill the W-2 fields if they're missing, else return it untouched.
  if (needsV2ToV3(raw)) {
    return {
      ...raw,
      notes: raw.notes ?? [],
      pinnedItemIds: raw.pinnedItemIds ?? [],
      itemMeta: raw.itemMeta ?? {},
      version: WORKSPACE_VERSION
    }
  }
  return raw
}
